import {getCurrencyForCountry} from './getPriceForCountry'
import {
  MC_PLANS,
  BC_PLANS,
  GROWTH_PACKAGES,
  SOCIAL_ADS_BNI_PLAN_CODES,
} from '../../../utils/constants'

// Ported from the legacy CC-upgrade-cart preview payload
// (src/screens/upgrade-order.js getPaymentPreview). Adapted here to
// the Proposal Tool's model (productCode/productType/quantity)
// instead of the raw cart's separate centers/apps/oneTimes arrays.

// Exported so buildSendLinkPayload.js's extractLocationDetail can identify
// a location's Thryv Leads item the same way this file does, instead of
// duplicating the code list.
export const TL_CODES = [
  'tl-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
  'tl-tax-5-thryv-m2m',
]

const VERTICAL_CLIENTSVCS_AND_PROSERVICES_CODES = [
  'vert_mc_clientsvcs-5-thryv-m2m',
  'vert_mc_web_clientsvcs-5-thryv-m2m',
  'vert_clientsvcs-5-thryv-m2m',
  'vert_mc_clientsvcs-5-thryv-ot',
  'vert_mc_web_clientsvcs-5-thryv-ot',
  'vert_clientsvcs-5-thryv-ot',
  'vert_svcs-5-thryv-m2m',
  'proservices-m2m',
]

const MONTHLY_KEAP_ADDONS_PARENT_CODES = [
  'addon-5-keap-addons',
  'addon-5-keap-addons-legacy-m2m',
]
const ANNUAL_KEAP_ADDONS_PARENT_CODES = [
  'addon-5-keap-addons-annual',
  'addon-5-keap-addons-legacy-annual',
]

// kp-zapier-750zaps-m2m is a Keap-partner entitlement, not a purchasable
// Recurly plan — the legacy code always stripped it back out of the
// marketplace addons after building them (see hasZapier below), so it's
// dropped up front here instead of round-tripped through the marketplace
// bucket.
const DROPPED_CODES = new Set(['kp-zapier-750zaps-m2m'])

// Every account needs exactly one 'cc_'-prefixed baseline parent plan and a
// marketplace subscription — mirrors client-payment.js's (Proposal Tool's
// sendPaymentLink) forced injection of both when the cart doesn't already
// contain one, since nothing in this cart UI lets the rep add/remove either.
// Gated behind `applyOrderDefaults` (see buildSubscriptionGroups) so it only
// applies at actual send-link/order time, not to the live cc/order/preview-
// payment pricing call — forcing these into every preview would show the
// rep line items (e-signatures, website builder) they never picked.
const DEFAULT_CC_PLAN_CODE = 'cc_basicpls-5-thryv-m2m'
const MARKETPLACE_CODE = 'appmarket-5-thryv-m2m'
// Bundled with every marketplace subscription regardless of selection —
// mirrors client-payment.js forcing these onto appmarket's addons even when
// neither was explicitly chosen.
const FORCED_MARKETPLACE_DEFAULT_CODES = [
  'addon-5-signatures_usage',
  'app-websitebuilder',
]

function withForcedMarketplaceDefaults(addons) {
  const codes = new Set(addons.map(a => a.code))
  const missing = FORCED_MARKETPLACE_DEFAULT_CODES.filter(
    code => !codes.has(code),
  ).map(code => ({code, quantity: 1}))
  return [...addons, ...missing]
}

// Products that never nest under 'appmarket-5-thryv-m2m' — either because
// they're grouped under a different parent above (Keap Max add-ons) or
// because they're always their own top-level subscription.
const MARKETPLACE_STANDALONE_CODES = new Set([
  'maxhvac-5-thryv-m2m',
  'maxroof-5-thryv-m2m',
  'maxelect-5-thryv-m2m',
  'maxplumb-5-thryv-m2m',
  'appmarket-5-thryv-m2m',
  ...MONTHLY_KEAP_ADDONS_PARENT_CODES,
  ...ANNUAL_KEAP_ADDONS_PARENT_CODES,
  'addon-5-leadslift-m2m',
  'addon-5-leadslift-bni-m2m',
  'tp_add-5-thryv-m2m',
  '247support-m2m',
  'addon-5-seokey-m2m',
  'addon-5-seopb-m2m',
  'seo-5-thryv-m2m',
  'seombg-5-thryv-m2m',
  'seobase-5-thryv-m2m',
  'seobase-5-thryv-bni-nt-m2m',
  ...TL_CODES,
  ...VERTICAL_CLIENTSVCS_AND_PROSERVICES_CODES,
])

const KCP_AU_QTY_MAPPING = [
  {amount: 418, qty: 380},
  {amount: 704, qty: 640},
  {amount: 1001, qty: 910},
  {amount: 2002, qty: 1820},
]

// optionValue encodes the Recurly quantity as its first segment ('3-39' → 3).
// For tiered pricing, the quantity is the number of seats, not the number of add-ons
// through the marketplace bucket.
function resolveQuantity(item) {
  const optionValue =
    item.configuration?.optionValue ?? item.configuredOptions?.optionValue
  if (optionValue) {
    const parsed = parseInt(optionValue.split('-')[0], 10)
    if (!isNaN(parsed)) return parsed
  }
  // recurlyQuantity is set by mapRecurlySubscriptionsToItems for already-included
  // Recurly subscriptions — it carries the raw Recurly quantity (e.g. 1000 for
  // plans like seobase where quantity encodes the dollar amount) without
  // polluting item.quantity, which display code uses via unitPrice × quantity.
  return item.recurlyQuantity ?? item.quantity ?? 1
}

function toSubscription(item) {
  return {
    code: item.productCode,
    quantity: resolveQuantity(item),
  }
}

// Items seeded from an existing Recurly subscription (mapRecurlySubscriptionsToItems.js)
// carry their already-included Recurly addOns on their own `addons` field
// (e.g. free/bundled codes like 'addon-5-signatures_usage', 'app-websitebuilder'
// nested under the account's 'appmarket-5-thryv-m2m' subscription) rather than
// as separate flat items. Every subscription entry built below must preserve
// these, not just the freshly-selected sibling items grouped below.
function ownAddons(item) {
  return (item.addons ?? []).map(toSubscription)
}

// Merges addon lists by code; later lists win on a duplicate code so a
// freshly-selected sibling item's quantity overrides a stale seeded one.
function mergeAddonLists(...lists) {
  const merged = new Map()
  lists.flat().forEach(addon => merged.set(addon.code, addon))
  return [...merged.values()]
}

function toSubscriptionWithOwnAddons(item) {
  return {
    ...toSubscription(item),
    addons: ownAddons(item),
  }
}

// Keap Max add-on children are identified the same way the legacy code did —
// via the catalog's parentProduct field (all5PlanPrices.apps there,
// productConfigInfo.apps here) — rather than a hardcoded code list, since
// the set of Keap add-ons changes often.
function findKeapAddonChildCodes(productConfigInfo, parentCodes) {
  const apps = productConfigInfo?.apps ?? []
  return apps
    .filter(
      app =>
        parentCodes.includes(app.parentProduct) &&
        !parentCodes.includes(app.code),
    )
    .map(app => app.code)
}

function buildKeapAddonsSubscription(apps, parentCodes, productConfigInfo) {
  const parent = apps.find(item => parentCodes.includes(item.productCode))
  if (!parent) return null

  const childCodes = findKeapAddonChildCodes(productConfigInfo, parentCodes)
  const siblingAddons = apps
    .filter(item => childCodes.includes(item.productCode))
    .map(toSubscription)

  return {
    subscription: {
      code: parent.productCode,
      quantity: 1,
      addons: mergeAddonLists(ownAddons(parent), siblingAddons),
    },
    childCodes,
  }
}

// Builds the childSubscriptions array: every center other than the account's
// core 'cc_'-prefixed plan is its own subscription (mirrors the legacy
// per-center loop), Keap Max add-ons nest under their monthly/annual
// (new-or-legacy) addon-parent plan, most other add-ons nest under
// 'appmarket-5-thryv-m2m', New Alpha SEO nests keyword/powerboost under
// itself, and old SEO / Thryv Leads / leadslift / vertical client services /
// proservices are always their own subscription.
function buildChildSubscriptions({
  apps,
  centers,
  nonCcCenters,
  productConfigInfo,
  isNewTLMCAccessible,
  frontendListingsBcMc,
  applyOrderDefaults,
}) {
  const isMCInCart = centers.some(item => MC_PLANS.includes(item.productCode))
  const isBCInCart = centers.some(item => BC_PLANS.includes(item.productCode))
  const isTLLeadExist =
    centers.some(item => TL_CODES.includes(item.productCode)) ||
    apps.some(item => TL_CODES.includes(item.productCode))

  const childSubscriptions = nonCcCenters.map(toSubscriptionWithOwnAddons)
  const claimedByKeap = new Set()

  const monthlyKeap = buildKeapAddonsSubscription(
    apps,
    MONTHLY_KEAP_ADDONS_PARENT_CODES,
    productConfigInfo,
  )
  if (monthlyKeap) {
    childSubscriptions.push(monthlyKeap.subscription)
    monthlyKeap.childCodes.forEach(code => claimedByKeap.add(code))
  }

  const annualKeap = buildKeapAddonsSubscription(
    apps,
    ANNUAL_KEAP_ADDONS_PARENT_CODES,
    productConfigInfo,
  )
  if (annualKeap) {
    childSubscriptions.push(annualKeap.subscription)
    annualKeap.childCodes.forEach(code => claimedByKeap.add(code))
  }

  // Marketplace add-ons — everything not claimed by Keap above, not
  // standalone, and not excluded by the BC/MC listings or TL-growth-package
  // rules.
  const seenMarketplaceCodes = new Set()
  const marketplaceSiblingAddons = apps
    .filter(item => {
      const code = item.productCode
      if (claimedByKeap.has(code)) return false
      if (
        code === 'app-listings' &&
        (isMCInCart || isBCInCart) &&
        frontendListingsBcMc
      ) {
        return false
      }
      if (
        isNewTLMCAccessible &&
        isTLLeadExist &&
        GROWTH_PACKAGES.includes(code)
      ) {
        return false
      }
      return !MARKETPLACE_STANDALONE_CODES.has(code)
    })
    .filter(item => {
      if (seenMarketplaceCodes.has(item.productCode)) return false
      seenMarketplaceCodes.add(item.productCode)
      return true
    })
    .map(toSubscription)

  const marketplaceApp = apps.find(
    item => item.productCode === MARKETPLACE_CODE,
  )
  const rawMarketplaceAddons = mergeAddonLists(
    marketplaceApp ? ownAddons(marketplaceApp) : [],
    marketplaceSiblingAddons,
  )
  const marketplaceAddons = applyOrderDefaults
    ? withForcedMarketplaceDefaults(rawMarketplaceAddons)
    : rawMarketplaceAddons
  // Only force the marketplace subscription to always exist at actual
  // order/send-link time (applyOrderDefaults) — see the comment on
  // DEFAULT_CC_PLAN_CODE/MARKETPLACE_CODE above.
  if (marketplaceApp || marketplaceAddons.length || applyOrderDefaults) {
    const existingMarketplace = childSubscriptions.find(
      sub => sub.code === MARKETPLACE_CODE,
    )
    if (existingMarketplace) {
      existingMarketplace.addons = mergeAddonLists(
        existingMarketplace.addons,
        marketplaceAddons,
      )
    } else {
      childSubscriptions.push({
        code: MARKETPLACE_CODE,
        quantity: marketplaceApp?.quantity ?? 1,
        addons: marketplaceAddons,
      })
    }
  }

  // New Alpha SEO — nest keyword/powerboost under it.
  const newAlphaSEO =
    centers.find(item => item.productCode === 'seobase-5-thryv-m2m') ??
    apps.find(item => item.productCode === 'seobase-5-thryv-m2m')
  if (newAlphaSEO) {
    // Keyword/powerboost can come from two shapes:
    //   - Proposal tool: nested in item.addons with a 'code' field (not 'productCode')
    //   - Legacy upgrade-order flow: separate top-level apps with 'productCode'
    const seoNestedAddons = (newAlphaSEO.addons ?? [])
      .filter(addon => {
        const code = addon.productCode ?? addon.code
        return code && (addon.quantity ?? 0) > 0
      })
      .map(addon => ({
        code: addon.productCode ?? addon.code,
        quantity: addon.quantity ?? 1,
      }))

    const keyword = apps.find(item => item.productCode === 'addon-5-seokey-m2m')
    const powerboost = apps.find(
      item => item.productCode === 'addon-5-seopb-m2m',
    )
    const topLevelAddons = []
    if (keyword) topLevelAddons.push(toSubscription(keyword))
    if (powerboost) topLevelAddons.push(toSubscription(powerboost))

    // seobase uses unitAmount=1 in Recurly; the dollar amount is the quantity.
    // After normalization via productSelectionRules.js, configuredOptions is stored
    // as `configuration` in Redux — check both to handle all call sites.
    // In the legacy upgrade-order flow the dollar amount was newAlphaSEO.amount.
    const seoQuantity =
      newAlphaSEO.configuration?.proposalAmount ||
      newAlphaSEO.configuredOptions?.proposalAmount ||
      newAlphaSEO.amount ||
      newAlphaSEO.quantity ||
      1

    childSubscriptions.push({
      code: newAlphaSEO.productCode,
      quantity: seoQuantity,
      addons: mergeAddonLists(seoNestedAddons, topLevelAddons),
    })
  }

  // Old SEO (only when New Alpha SEO isn't in play) / Thryv Leads /
  // leadslift / vertical client services / proservices are always their own
  // top-level subscription rather than nested addons.
  const alwaysStandaloneCodes = new Set([
    ...(newAlphaSEO ? [] : ['seo-5-thryv-m2m', 'seombg-5-thryv-m2m']),
    ...TL_CODES,
    'addon-5-leadslift-m2m',
    'addon-5-leadslift-bni-m2m',
    'seobase-5-thryv-bni-nt-m2m',
    ...VERTICAL_CLIENTSVCS_AND_PROSERVICES_CODES,
  ])
  apps.forEach(item => {
    if (alwaysStandaloneCodes.has(item.productCode)) {
      childSubscriptions.push(toSubscriptionWithOwnAddons(item))
    }
  })

  return childSubscriptions
}

// AU/NZ prices the Keap Certified Partner fee in "impressions", not dollars —
// remap the raw quantity to the tiered AU quantity Recurly expects.
function applyAuKcpQuantityMapping(oneTimeFees, currency) {
  if (currency !== 'AUD') return oneTimeFees
  return oneTimeFees.map(item => {
    if (item.code !== 'keap_kcp-cert-5-fee') return item
    const matched = KCP_AU_QTY_MAPPING.find(
      mapItem => Number(mapItem.amount) === Number(item.quantity),
    )
    return matched ? {...item, quantity: matched.qty} : item
  })
}

// Splits a normalized item list (productCode/productType/quantity — the API
// schema shape shared by Proposal Tool and CatalogSelection) into
// parentSubscriptions/childSubscriptions/oneTimeFees, with all the same
// product-specific nesting rules (Keap Max add-ons, marketplace add-ons, New
// Alpha SEO) applied. Exported so any caller that needs this same grouping
// for a differently-shaped request body (e.g. PurchaseLinkBuilder's
// buildSendLinkPayload, which additionally tags each line with a
// per-location locationThryvId) doesn't have to re-derive it.
//
// productConfigInfo ({centers, apps}) is the catalog data used to resolve
// Keap Max add-on parents. isNewTLMCAccessible/frontendListingsBcMc mirror
// the same-named flags in the legacy upgrade-cart flow (LaunchDarkly +
// account entitlements) and gate the TL-growth-package and BC/MC-listings
// exclusion rules the same way.
export function buildSubscriptionGroups({
  items = [],
  productConfigInfo,
  isNewTLMCAccessible = false,
  frontendListingsBcMc = false,
  // See DEFAULT_CC_PLAN_CODE/MARKETPLACE_CODE above — only the actual send-
  // link/order-creation caller (buildSendLinkPayload) sets this; the live
  // cc/order/preview-payment caller (buildPaymentPreviewPayload below) never
  // does, so pricing preview keeps showing only what the rep actually picked.
  applyOrderDefaults = false,
}) {
  const centers = items.filter(item => item.productType === 'CENTER')
  // Only the account's core 'cc_'-prefixed plan is a parentSubscription —
  // every other center (MC, BC, RC, WC, ...) is its own childSubscription,
  // same as the legacy CC-upgrade-cart preview payload.
  const ccCentersFromCart = centers.filter(item =>
    item.productCode.startsWith('cc_'),
  )
  // Mirrors client-payment.js's forced cc_basicpls injection: a cart built
  // from scratch never contains this baseline plan on its own, since
  // nothing in the UI lets the rep add/remove it.
  const ccCenters =
    applyOrderDefaults && ccCentersFromCart.length === 0
      ? [
          {
            productCode: DEFAULT_CC_PLAN_CODE,
            productType: 'CENTER',
            quantity: 1,
          },
        ]
      : ccCentersFromCart
  const nonCcCenters = centers.filter(
    item => !item.productCode.startsWith('cc_'),
  )
  const apps = items.filter(
    item =>
      item.productType === 'APP' &&
      (!SOCIAL_ADS_BNI_PLAN_CODES.includes(item.productCode) ||
        item.productCode === 'seobase-5-thryv-bni-nt-m2m') &&
      !DROPPED_CODES.has(item.productCode),
  )
  const oneTimeFees = items
    .filter(item => item.productType === 'ONE_TIME_FEE')
    .map(toSubscription)

  return {
    parentSubscriptions: ccCenters.map(toSubscriptionWithOwnAddons),
    childSubscriptions: buildChildSubscriptions({
      apps,
      centers,
      nonCcCenters,
      productConfigInfo,
      isNewTLMCAccessible,
      frontendListingsBcMc,
      applyOrderDefaults,
    }),
    oneTimeFees,
  }
}

// Builds the request body for `cc/order/preview-payment`. Reusable by any
// flow that needs live, backend-computed pricing for a set of selected
// products.
export function buildPaymentPreviewPayload({
  items = [],
  thryvId,
  country,
  coupons = [],
  productConfigInfo,
  isNewTLMCAccessible = false,
  frontendListingsBcMc = false,
}) {
  const {parentSubscriptions, childSubscriptions, oneTimeFees} =
    buildSubscriptionGroups({
      items,
      productConfigInfo,
      isNewTLMCAccessible,
      frontendListingsBcMc,
    })

  const currency = getCurrencyForCountry(country)

  return {
    parentSubscriptions,
    childSubscriptions,
    oneTimeFees: applyAuKcpQuantityMapping(oneTimeFees, currency),
    thryvId,
    currency,
    coupons,
  }
}
